/**
 * Demo-seed endpoints.
 *
 * Wraps the synthetic-data loader routes (`POST /demo-seed/load`,
 * `POST /demo-seed/switch-to-real`) on the typed `http.*` client so the
 * onboarding chat flow inherits the auth-refresh + 401 single-flight retry
 * instead of issuing raw `fetch()` calls that bypass it (#3268).
 *
 * These are the ROOT-mounted variants (`/demo-seed/*`). The gateway resolves
 * the per-user workspace from the `x-workspace-id` header that `http.*`
 * always sends, so they are byte-identical in destination to the raw-fetch
 * call sites they replace — the calls just gain token rotation.
 *
 * Hand-typed shapes: `xavior-openapi.yaml` has no `/demo-seed` paths, so there
 * are no generated `@xavior/contracts` types for these routes (same precedent
 * as endpoints/email-module.ts).
 */
import { http } from '@/lib/http'

/**
 * `POST /demo-seed/load` — load the synthetic Boris workspace into the
 * caller's per-user DB. Throws `HttpError` on a non-2xx response so callers'
 * retry loops can treat a failed attempt uniformly (network throw === HTTP
 * error). Returns the parsed JSON body on success.
 */
export async function loadDemoSeed(authToken?: string | null): Promise<unknown> {
  const res = await http.post<unknown>('/demo-seed/load', {}, { authToken })
  return res.data
}

/** Result of wiping the demo dataset before connecting a real account. */
export interface SwitchToRealResult {
  messagesRemoved: number
  contactsRemoved: number
  [key: string]: unknown
}

/**
 * `POST /demo-seed/switch-to-real` — wipe ALL demo data (emails, threads,
 * integration row, demo-extracted contacts) ahead of the real Gmail OAuth.
 */
export async function switchDemoToReal(
  authToken?: string | null,
): Promise<SwitchToRealResult> {
  const res = await http.post<SwitchToRealResult>('/demo-seed/switch-to-real', {}, { authToken })
  return res.data
}

export const demoSeed = { loadDemoSeed, switchDemoToReal }
